import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';
import { Button } from '@/shared/ui/components/shadcn/button';
import { Image, Text, TextInput, Title } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { VistoryReport } from '@/features/vistories/services/public-vistory-service';
import { DateField } from '@/shared/ui/components/form/inputs';

interface AppointmentSlot {
  date: string;
  time: string;
  note?: string;
}

interface Props {
  report: VistoryReport;
  onApprove: () => void;
  onReject: (reason: string, appointments: AppointmentSlot[]) => void;
  loading: boolean;
}

/**
 * Read-only vistory report presented to the tenant, with approve/reject actions.
 */
export function VistoryReportView({ report, onApprove, onReject, loading }: Props) {
  const { t } = useTranslation('features/vistories');
  const [showRejectForm, setShowRejectForm] = useState(false);

  return (
    <div data-testid="vistory-report-view" className="flex flex-col gap-[var(--space-6)]">
      <ReportHeader report={report} />
      <ReportPhotos report={report} />

      {!showRejectForm ? (
        <div className="flex flex-col gap-[var(--space-3)] sm:flex-row sm:justify-end">
          <Button variant="destructive" onClick={() => setShowRejectForm(true)} disabled={loading}>
            {t('vistory_public.reject_button')}
          </Button>
          <Button onClick={onApprove} disabled={loading} data-testid="vistory-approve-btn">
            {loading ? t('vistory_public.processing') : t('vistory_public.approve_button')}
          </Button>
        </div>
      ) : (
        <RejectForm
          onSubmit={(reason, appointments) => onReject(reason, appointments)}
          onCancel={() => setShowRejectForm(false)}
          loading={loading}
        />
      )}
    </div>
  );
}

/**
 * Header block of the public vistory report showing title and creation date.
 */
function ReportHeader({ report }: { report: VistoryReport }) {
  const { t } = useTranslation('features/vistories');
  const createdDate = formatLocalizedDate(report.created_at);
  return (
    <div>
      <Title order={1} size="xl" className="text-[var(--text-primary)]">{report.title}</Title>
      {report.description && (
        <Text size="sm" className="mt-[var(--space-2)] text-[var(--text-secondary)]">
          {report.description}
        </Text>
      )}
      <Text size="xs" className="mt-[var(--space-1)] text-[var(--text-tertiary)]">
        {t('vistory_public.created_at', { date: createdDate })}
      </Text>
    </div>
  );
}

/**
 * Renders the report's photo grid, or nothing when there are no photos.
 */
function ReportPhotos({ report }: { report: VistoryReport }) {
  if (report.photos.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-[var(--space-2)] sm:grid-cols-3 md:grid-cols-4">
      {report.photos.map((photo, index) => (
        <Image
          // The public payload carries no `key` — it never did — so the
          // filename plus position is the only stable identity here.
          key={`${photo.filename}-${index}`}
          src={photo.url}
          alt={photo.filename}
          className="aspect-square w-full rounded-(--radius-md) object-cover border border-[var(--border-subtle)]"
        />
      ))}
    </div>
  );
}

/**
 * Form letting the client reject a report with a reason and proposed appointment slots.
 */
function RejectForm({ onSubmit, onCancel, loading }: {
  onSubmit: (reason: string, appointments: AppointmentSlot[]) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation('features/vistories');
  const [reason, setReason] = useState('');
  const [slots, setSlots] = useState<AppointmentSlot[]>([{ date: '', time: '' }]);

  function updateSlot(idx: number, field: keyof AppointmentSlot, value: string) {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  }

  function addSlot() {
    if (slots.length < 3) setSlots([...slots, { date: '', time: '' }]);
  }

  function removeSlot(idx: number) {
    setSlots(prev => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit() {
    if (!reason.trim()) return;
    const validSlots = slots.filter(s => s.date && s.time);
    onSubmit(reason.trim(), validSlots);
  }

  return (
    <div className={cn(
      'flex flex-col gap-[var(--space-3)] rounded-(--radius-lg) border',
      'border-[var(--border-subtle)] bg-[var(--surface-secondary)] p-[var(--space-4)]',
    )}>
      <TextInput
        asTextArea
        label={t('vistory_public.reject_reason_label')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={t('vistory_public.reject_reason_placeholder')}
      />
      <div className="flex flex-col gap-[var(--space-2)]">
        <Text size="xs" fw={500} className="text-[var(--text-secondary)]">
          {t('vistory_public.appointments_label')}
        </Text>
        {slots.map((slot, idx) => (
          <div key={idx} className="flex gap-[var(--space-2)] items-end">
            <DateField
              label={t('vistory_public.appointment_date')}
              value={slot.date}
              onChange={(e) => updateSlot(idx, 'date', e.target.value)}
              className="flex-1"
            />
            <TextInput
              label={t('vistory_public.appointment_time')}
              type="time"
              value={slot.time}
              onChange={(e) => updateSlot(idx, 'time', e.target.value)}
              className="flex-1"
            />
            {slots.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => removeSlot(idx)}>✕</Button>
            )}
          </div>
        ))}
        {slots.length < 3 && (
          <Button variant="outline" size="sm" onClick={addSlot}>
            {t('vistory_public.add_appointment')}
          </Button>
        )}
      </div>
      <div className="flex gap-[var(--space-3)] justify-end">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          {t('vistory_public.cancel')}
        </Button>
        <Button variant="destructive" onClick={handleSubmit} disabled={!reason.trim() || loading}>
          {loading ? t('vistory_public.processing') : t('vistory_public.confirm_reject')}
        </Button>
      </div>
    </div>
  );
}
